import React, { useEffect, useState } from 'react';
import { Popin, PopinFooter } from '../../design-system/components/Popin';
import { Button } from '../../design-system/components/Button';
import { useCookieConsentStore } from './useCookieConsentStore';
import './CookiePreferencesPopin.css';

/* ============================================================
   CookiePreferencesPopin — réglages du consentement cookies.
   Ouverte depuis la bannière (bouton Customize) et depuis la
   page /cookies-settings (lien du footer). Le choix est persisté
   (useCookieConsentStore) et pilote Google Analytics
   (initAnalytics — prod uniquement).
   ============================================================ */
interface CookiePreferencesPopinProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CookiePreferencesPopin: React.FC<CookiePreferencesPopinProps> = ({
  isOpen,
  onClose,
}) => {
  const analytics = useCookieConsentStore((s) => s.analytics);
  const acceptAll = useCookieConsentStore((s) => s.acceptAll);
  const rejectNonEssential = useCookieConsentStore((s) => s.rejectNonEssential);
  const savePreferences = useCookieConsentStore((s) => s.savePreferences);

  // Position du toggle, recalée sur le choix enregistré à chaque ouverture
  const [analyticsEnabled, setAnalyticsEnabled] = useState(analytics ?? false);
  useEffect(() => {
    if (isOpen) setAnalyticsEnabled(analytics ?? false);
  }, [isOpen, analytics]);

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  const handleReject = () => {
    rejectNonEssential();
    onClose();
  };

  const handleSave = () => {
    savePreferences(analyticsEnabled);
    onClose();
  };

  return (
    <Popin
      isOpen={isOpen}
      onClose={onClose}
      title="Cookie Preferences"
      size="md"
      footer={
        <PopinFooter>
          <Button variant="ghost" size="md" onClick={handleReject}>
            Reject Non-Essential
          </Button>
          <Button variant="outline" size="md" onClick={handleAcceptAll}>
            Accept All
          </Button>
          <Button variant="primary" size="md" onClick={handleSave}>
            Save Preferences
          </Button>
        </PopinFooter>
      }
    >
      <p className="CookiePreferencesPopin__intro">
        We use essential cookies to run the platform and optional analytics cookies to improve
        our software.
      </p>

      {/* Cookies strictement nécessaires — toujours actifs */}
      <div className="CookiePreferencesPopin__option">
        <div className="CookiePreferencesPopin__option-header">
          <span className="CookiePreferencesPopin__option-name">Strictly Necessary Cookies</span>
          <span className="CookiePreferencesPopin__badge">Always active</span>
        </div>
        <p className="CookiePreferencesPopin__option-desc">
          Essential for logging in, account security, and running prompt workflows.
        </p>
      </div>

      {/* Cookies analytics — toggle utilisateur */}
      <div className="CookiePreferencesPopin__option">
        <div className="CookiePreferencesPopin__option-header">
          <span className="CookiePreferencesPopin__option-name">
            Analytics Cookies (Google Analytics)
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={analyticsEnabled}
            aria-label="Analytics Cookies"
            className={`CookiePreferencesPopin__switch${
              analyticsEnabled ? ' CookiePreferencesPopin__switch--on' : ''
            }`}
            onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
          >
            <span className="CookiePreferencesPopin__switch-thumb" />
          </button>
        </div>
        <p className="CookiePreferencesPopin__option-desc">
          Helps us understand platform usage and optimize performance. No personal data is sold
          or shared for advertising.
        </p>
      </div>
    </Popin>
  );
};

export default CookiePreferencesPopin;
